import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { resolve } from '@cvent/nucleus-dynamic-css';
import { getElementSize } from '../utils/getElementSize';
import { getFloatPosition } from '../utils/getFloatPosition';
import getBrowserDimensions from '../utils/getBrowserDimensions';

/**
  Informational Flyout Message Component that is used with InfoFlyout.js.
  Float direction calculated by postition, message size and browser viewport size.
  Basic calculations for mobile browsers.
**/
export default class FlyoutMessage extends React.Component {
  static displayName = 'FlyoutMessage';
  static propTypes = {
    /** The child content for the flyout message. */
    children: PropTypes.node.isRequired,
    /** The dimensions of the trigger object. */
    triggerSize: PropTypes.func,
    /** The offset position applied to the flyout based on arrow size - default to 23px */
    flyoutOffset: PropTypes.number,
    /** Which direction does it flow from. */
    flyoutDirection: PropTypes.oneOf([
      'horizontal', 'vertical'
    ]),
    /** Allows a user to override the direction in which a flyout flows */
    forceDirection: PropTypes.shape({
      horz: PropTypes.oneOf([
        'left', 'right', 'center'
      ]),
      vert: PropTypes.oneOf([
        'top', 'bottom'
      ])
    }),
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Top level style applied to the container. */
      container: PropTypes.object,
      /** Style applied to the arrow element. */
      arrow: PropTypes.object,
      /** Style applied for topleft position. */
      topleft: PropTypes.object,
      /** Style applied for topright position. */
      topright: PropTypes.object,
      /** Style applied for bottomleft position. */
      bottomleft: PropTypes.object,
      /** Style applied for bottomright position. */
      bottomright: PropTypes.object
    })
  };

  static defaultProps = {
    flyoutOffset: 23
  };

  componentDidMount() {
    // Get elements for inline and className change based on position
    const flyoutElement = ReactDOM.findDOMNode(this.refs.flyout);
    const arrowElement = ReactDOM.findDOMNode(this.refs.arrow);
    const { flyoutDirection, flyoutOffset } = this.props;
    // Calculate float position based on element to browser viewport
    const myFloatPosition = [];
    const FloatPosition = getFloatPosition(flyoutElement);
    // Check for overrides before setting float position
    myFloatPosition.vert = this.props.forceDirection.vert || FloatPosition.vert;
    myFloatPosition.horz = this.props.forceDirection.horz || FloatPosition.horz;
    const flyoutRect = getElementSize(flyoutElement);
    const elementBoundingClientRect = flyoutElement.getBoundingClientRect();
    const browserMeasures = getBrowserDimensions(window, document);

    // Add class to arrow for position
    let arrowClasses;
    if (flyoutDirection === 'horizontal') {
      // Horizontal Position flyout on which direction, size and flyoutOffset //
      flyoutElement.style.top = (myFloatPosition.vert === 'bottom') ?
        `-${flyoutOffset / 1.5}px` : 'auto';
      flyoutElement.style.bottom = (myFloatPosition.vert === 'top') ?
        `-${flyoutOffset / 1.5}px` : 'auto';

      flyoutElement.style.left = (myFloatPosition.horz === 'left') ?
      `-${flyoutRect.width + flyoutOffset}px` : 'auto';
      flyoutElement.style.right = (myFloatPosition.horz === 'right') ?
      `-${flyoutRect.width + flyoutOffset}px` : 'auto';
    } else {
      // Vertical Position flyout on which direction, size and flyoutOffset //
      flyoutElement.style.bottom = (myFloatPosition.vert === 'bottom') ?
        `-${flyoutRect.height + (flyoutOffset / 2)}px` : 'auto';
      flyoutElement.style.top = (myFloatPosition.vert === 'top') ?
        `-${flyoutRect.height + (flyoutOffset / 2)}px` : 'auto';

      flyoutElement.style.left = 'auto';
      flyoutElement.style.right = 'auto';
      if (myFloatPosition.horz === 'right') {
        flyoutElement.style.left = `-${flyoutOffset}px`;
      } else if (myFloatPosition.horz === 'left') {
        flyoutElement.style.right = `-${flyoutOffset}px`;
      } else if (this.props.forceDirection.horz === 'center') {
        myFloatPosition.horz = 'center';
        if (elementBoundingClientRect.left < 0) {
          // Move flyout to the right
          myFloatPosition.horz = 'right';
          flyoutElement.style.transform = 'none';
          flyoutElement.style.marginLeft = 'auto';
          flyoutElement.style.left = `-${flyoutOffset}px`;
        } else if (elementBoundingClientRect.left + elementBoundingClientRect.width
          > browserMeasures.browserWidth) {
          // Move flyout to the left
          myFloatPosition.horz = 'left';
          flyoutElement.style.transform = 'none';
          flyoutElement.style.right = `-${flyoutOffset}px`;
        }
      }
    }

    arrowClasses = resolve(this.props, flyoutDirection, myFloatPosition.vert + myFloatPosition.horz);
    arrowElement.className = arrowElement.className + ' ' + arrowClasses.className;
  }

  render() {
    const { flyoutDirection, forceDirection: { horz } } = this.props;
    const key = flyoutDirection === 'vertical' && horz === 'center' ? 'horizontallyCentered' : '';
    const styles = resolve(this.props, 'container', key);
    return (
      <div {...styles} ref="flyout">
        <span {...resolve(this.props, 'arrow')} ref="arrow"></span>
        {this.props.children}
      </div>
    );
  }
}

export const styleKeys = [
  'container',
  'horizontallyCentered',
  'arrow',
  'vertical',
  'topcenter',
  'bottomcenter',
  'horizontal',
  'bottomright',
  'bottomleft',
  'topright',
  'topleft'
];
